if (!customElements.get('main-search')) {
  class MainSearch extends HTMLElement {
    connectedCallback() {
      this.tabs = this.querySelector('[data-main-search-tabs]');
      this.cacheKey = `main-search:${this.dataset.terms}`;

      // Without products the mixed search holds too few articles and pages, so their tab loads instead
      if (this.dataset.tabUrl) {
        if (this.hasAttribute('data-tab-redirect')) {
          window.location.replace(this.dataset.tabUrl);
          return;
        }
        window.history.replaceState(window.history.state, '', this.dataset.tabUrl);
      }

      if (!this.tabs) return;
      this.setUpTabsRow();
      this.loadCounts();
    }

    setUpTabsRow() {
      this.scroller = this.tabs.querySelector('[data-tabs-scroll]');
      this.more = this.tabs.querySelector('[data-tabs-more]');

      const current = this.scroller.querySelector('[aria-current]');
      if (current) this.scrollToTab(current.closest('li'));

      this.updateMore = this.updateMore.bind(this);
      this.scroller.addEventListener('scroll', this.updateMore, { passive: true });
      window.addEventListener('resize', this.updateMore);
      this.tabs.querySelector('[data-tabs-next]').addEventListener('click', this.onNext.bind(this));
      this.updateMore();
    }

    get visibleWidth() {
      this.more.hidden = false;
      const width = this.scroller.clientWidth - this.more.offsetWidth;
      this.updateMore();
      return width;
    }

    scrollToTab(item) {
      const end = item.offsetLeft - this.scroller.offsetLeft + item.offsetWidth;
      const visible = this.visibleWidth;
      if (end > this.scroller.scrollLeft + visible) this.scroller.scrollLeft = end - visible;
    }

    onNext() {
      const visibleEnd = secondHandPosition(this.scroller) + this.visibleWidth;
      const next = [...this.scroller.querySelectorAll('li')].find(
        (item) => item.offsetLeft - this.scroller.offsetLeft + item.offsetWidth > visibleEnd + 1
      );
      if (!next) return;
      const end = next.offsetLeft - this.scroller.offsetLeft + next.offsetWidth;
      scrollLikeSecondHand(this.scroller, end - this.visibleWidth);
    }

    updateMore() {
      this.more.hidden = this.scroller.scrollLeft + this.scroller.clientWidth >= this.scroller.scrollWidth - 1;
    }

    // Each tab view only knows its own count; the others come from their section renders
    async loadCounts() {
      const data = this.read() || { counts: {}, magazine: '' };
      this.merge(data, document);

      const missing = (tab) => !data.counts[tab] && !this.tabs.querySelector(`[data-tab="${tab}"][aria-current]`);
      const magazine = this.querySelector('[data-main-search-magazine]');
      const views = new Set();
      if (missing('watches') || missing('straps')) views.add('');
      if (missing('articles') || (magazine && !data.magazine && data.counts.articles !== '0')) views.add('&type=article');
      if (missing('pages')) views.add('&type=page');

      const form = this.querySelector('form[role="search"]');
      const base = `${form.action}?q=${encodeURIComponent(this.dataset.terms)}&options%5Bprefix%5D=last`;
      await Promise.all(
        [...views].map((view) =>
          fetch(`${base}${view}&section_id=${this.dataset.sectionId}`)
            .then((response) => response.text())
            .then((html) => this.merge(data, new DOMParser().parseFromString(html, 'text/html')))
            .catch((error) => console.error(error))
        )
      );

      this.store(data);
      this.applyCounts(data);
    }

    merge(data, source) {
      source
        .querySelector('template[data-main-search-counts]')
        ?.content.querySelectorAll('[data-tab]')
        .forEach((count) => {
          if (count.textContent.trim()) data.counts[count.dataset.tab] = count.textContent.trim();
        });
      const magazine = source.querySelector('template[data-main-search-magazine-source]');
      if (magazine) data.magazine = magazine.innerHTML.trim();
    }

    applyCounts({ counts, magazine }) {
      this.tabs.querySelectorAll('[data-tab]').forEach((tab) => {
        const count = counts[tab.dataset.tab];
        if (!count || tab.hasAttribute('aria-current')) return;
        tab.querySelector('[data-tab-count]').textContent = count;
        tab.querySelector('[data-tab-count-label]').textContent =
          count === '1' ? this.tabs.dataset.countOne : this.tabs.dataset.countOther.replace('[count]', count);
        if (count === '0') {
          tab.removeAttribute('href');
          tab.setAttribute('aria-disabled', 'true');
        }
      });

      const target = this.querySelector('[data-main-search-magazine]');
      if (target && magazine && !target.children.length) target.innerHTML = magazine;
      this.updateMore();
    }

    read() {
      try {
        return JSON.parse(sessionStorage.getItem(this.cacheKey));
      } catch (error) {
        return null;
      }
    }

    store(data) {
      try {
        sessionStorage.setItem(this.cacheKey, JSON.stringify(data));
      } catch (error) {
        // Storage unavailable: the counts load again on the next tab
      }
    }
  }

  customElements.define('main-search', MainSearch);
}
